import React from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaArrowLeft,
  FaCalendar,
  FaClock,
  FaUser,
  FaSpider,
  FaShare,
} from "react-icons/fa";
import Seo, { SITE_URL } from "./Seo";
import { blogPostsWithSlugs } from "./blogdata";

const BlogPost = () => {
  const { slug } = useParams();

  const post = React.useMemo(
    () => blogPostsWithSlugs.find((entry) => entry.slug === slug),
    [slug],
  );

  const relatedPosts = React.useMemo(
    () => blogPostsWithSlugs.filter((p) => p.slug !== slug).slice(0, 3),
    [slug],
  );

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // ── 404 State ──
  if (!post) {
    return (
      <div className="relative min-h-screen bg-dark-900/40 text-light-100 overflow-hidden">
        <Seo
          title="Blog Post Not Found | Aashish Timsina"
          description="The requested blog post could not be found. Explore web scraping and backend development articles by Aashish Timsina."
          path="/blog"
          noIndex
        />
        <div className="relative z-10 container mx-auto px-6 py-40 text-center">
          <div className="inline-flex p-4 rounded-full bg-primary-500/10 border border-primary-500/20 mb-6">
            <FaSpider className="h-10 w-10 text-primary-400" />
          </div>
          <h1 className="text-4xl font-bold font-space-grotesk mb-4">
            Post Not Found
          </h1>
          <p className="text-light-200 mb-8 text-lg">
            The article you are looking for does not exist.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary-500/10 border border-primary-500/30 text-primary-400 hover:bg-primary-500/20 transition-all duration-300"
          >
            <FaArrowLeft />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-dark-900/40 text-light-100 overflow-hidden">
      <Seo
        title={`${post.title} | Web Scraping & Backend Blog | Aashish Timsina`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        type="article"
        publishedTime={post.date}
        image={`${SITE_URL}${post.image}`}
        imageAlt={post.title}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          datePublished: post.date,
          dateModified: post.date,
          keywords: post.tags.join(", "),
          image: `${SITE_URL}${post.image}`,
          mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
          author: {
            "@type": "Person",
            name: post.author,
            url: SITE_URL,
          },
          publisher: { "@type": "Person", name: "Aashish Timsina", url: SITE_URL },
        }}
      />

      {/* Ambient glows */}

      <div className="relative z-10">
        <div className="container mx-auto px-6 py-12">
          {/* ── Back Navigation ── */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-primary-400 transition-colors mb-12 group"
            >
              <FaArrowLeft className="group-hover:-translate-x-1 transition-transform duration-300" />
              Back to Blog
            </Link>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {/* ── Article Header ── */}
            <motion.header
              className="mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Tags row */}
              <div className="flex flex-wrap gap-2 mb-6">
                {post.tags.slice(0, 4).map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-primary-500/10 text-primary-400 border border-primary-500/20 rounded-full text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
                {post.tags.length > 4 && (
                  <span className="px-3 py-1 bg-pink-500/10 text-pink-400 border border-pink-500/20 rounded-full text-xs">
                    +{post.tags.length - 4} more
                  </span>
                )}
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-5xl font-bold font-space-grotesk mb-6 leading-tight gradient-text">
                {post.title}
              </h1>

              {/* Excerpt */}
              <p className="text-lg text-light-200 leading-relaxed mb-8 border-l-2 border-primary-500/40 pl-4">
                {post.excerpt}
              </p>

              {/* Meta bar */}
              <div className="flex flex-wrap items-center justify-between gap-4 p-4 rounded-xl bg-dark-800/60 border border-primary-500/10">
                <div className="flex flex-wrap items-center gap-5 text-sm text-light-300">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary-500 to-pink-500 flex items-center justify-center">
                      <FaUser className="h-3 w-3 text-white" />
                    </div>
                    <span className="text-light-100 font-medium">
                      {post.author}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaCalendar className="text-primary-400" />
                    <span>{formatDate(post.date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaClock className="text-primary-400" />
                    <span>{post.readTime}</span>
                  </div>
                </div>

                {/* Share button */}
                <button
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: post.title,
                        url: window.location.href,
                      });
                    } else {
                      navigator.clipboard.writeText(window.location.href);
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm hover:bg-primary-500/20 transition-all duration-300"
                >
                  <FaShare className="h-3 w-3" />
                  Share
                </button>
              </div>
            </motion.header>

            {/* ── Divider ── */}
            <div className="h-px bg-gradient-to-r from-transparent via-primary-500/30 to-transparent mb-12" />

            {/* ── Article Body ── */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div
                className="
                  prose prose-invert prose-lg max-w-none
                  prose-headings:text-light-100
                  prose-headings:font-space-grotesk
                  prose-headings:font-bold
                  prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-light-200
                  prose-p:leading-relaxed
                  prose-p:mb-5
                  prose-strong:text-primary-400
                  prose-a:text-primary-400 prose-a:no-underline hover:prose-a:underline
                  prose-code:text-primary-300
                  prose-code:bg-dark-800
                  prose-code:px-1.5
                  prose-code:py-0.5
                  prose-code:rounded
                  prose-code:text-sm
                  prose-code:border
                  prose-code:border-primary-500/20
                  prose-pre:bg-dark-800
                  prose-pre:border
                  prose-pre:border-primary-500/20
                  prose-pre:rounded-xl
                  prose-pre:shadow-lg
                  prose-ul:text-light-200
                  prose-ol:text-light-200
                  prose-li:text-light-200
                  prose-li:mb-1
                  prose-blockquote:border-l-primary-500
                  prose-blockquote:text-light-300
                  prose-blockquote:bg-dark-800/40
                  prose-blockquote:rounded-r-xl
                  prose-blockquote:py-1
                  prose-blockquote:pr-4
                "
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </motion.div>

            {/* ── Bottom Divider ── */}
            <div className="h-px bg-gradient-to-r from-transparent via-primary-500/30 to-transparent mt-16 mb-12" />

            {/* ── Author Card ── */}
            <motion.div
              className="p-6 rounded-lg border border-primary-500/10 bg-dark-800/60 flex flex-col sm:flex-row items-start sm:items-center gap-5 mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                <FaUser className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-primary-400 mb-1">
                  Written by
                </p>
                <h3 className="text-lg font-bold font-space-grotesk text-light-100 mb-1">
                  {post.author}
                </h3>
                <p className="text-sm text-light-200">
                  Web scraper and backend developer based in Nepal — building
                  automated data extraction pipelines, SERP scrapers, and
                  scalable Node.js systems.
                </p>
              </div>
            </motion.div>

            {/* ── Related Posts ── */}
            {relatedPosts.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center gap-3 mb-8">
                  <div className="h-px flex-1 bg-primary-500/20" />
                  <span className="text-xs uppercase tracking-widest text-primary-400 px-3">
                    Related Articles
                  </span>
                  <div className="h-px flex-1 bg-primary-500/20" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedPosts.map((related) => (
                    <Link
                      key={related.id}
                      to={`/blog/${related.slug}`}
                      className="group block p-5 rounded-lg border border-primary-500/10 bg-dark-800/40 hover:border-primary-500/30 transition-all duration-300 hover:shadow-lg hover:shadow-primary-500/5"
                    >
                      <div className="flex flex-wrap gap-1 mb-3">
                        {related.tags.slice(0, 2).map((tag, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 text-xs rounded-full bg-primary-500/10 text-primary-400 border border-primary-500/20"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h4 className="text-sm font-bold font-space-grotesk text-light-100 group-hover:text-primary-400 transition-colors leading-snug mb-3 line-clamp-2">
                        {related.title}
                      </h4>
                      <div className="flex items-center gap-1 text-xs text-primary-400 font-medium">
                        <span>Read</span>
                        <FaArrowLeft className="rotate-180 group-hover:translate-x-1 transition-transform duration-300" />
                      </div>
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}

            {/* ── CTA ── */}
            <motion.div
              className="mt-16 p-8 rounded-lg border border-primary-500/10 bg-gradient-to-br from-dark-800/80 to-dark-700/30 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex justify-center mb-4">
                <div className="p-3 rounded-full bg-primary-500/10 border border-primary-500/20">
                  <FaSpider className="h-7 w-7 text-primary-400" />
                </div>
              </div>
              <h3 className="text-2xl font-bold font-space-grotesk mb-3">
                Need a Custom{" "}
                <span className="text-primary-400">Web Scraping Solution?</span>
              </h3>
              <p className="text-light-200 mb-6 max-w-xl mx-auto">
                Based in Nepal and working with clients worldwide — let's build
                a reliable data extraction pipeline tailored to your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  className="px-6 py-3 bg-gradient-to-r from-primary-500 to-pink-500 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-primary-500/25 transition-all duration-300"
                  onClick={() => (window.location.href = "/contact")}
                >
                  Get In Touch
                </motion.button>
                <motion.button
                  className="px-6 py-3 rounded-xl border border-primary-500/30 text-primary-400 hover:bg-primary-500/10 transition-all duration-300"
                  onClick={() => (window.location.href = "/webscraper")}
                >
                  View Scraping Services
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
